import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

// Importações do Leaflet
let L: any;
try {
  L = require('leaflet');
  require('leaflet/dist/leaflet.css');
} catch (e) {
  console.error('Erro ao carregar o Leaflet:', e);
}

// Tipos para o componente
interface Location {
  lat: number;
  lng: number;
}

interface Waypoint {
  id: string;
  title: string;
  address?: string;
  location: Location;
}

interface SimpleRouteMapProps {
  waypoints: Waypoint[];
  center?: Location;
  zoom?: number;
}

/**
 * Componente de mapa simplificado para exibir pontos e uma linha reta entre eles
 */
const SimpleRouteMap: React.FC<SimpleRouteMapProps> = ({ 
  waypoints = [], 
  center,
  zoom = 12
}) => {
  // Refs para elementos DOM e instâncias do Leaflet
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  
  // Estados para controle de UI
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Calcular o centro do mapa
  const mapCenter = React.useMemo(() => {
    // Centro padrão (São Paulo)
    const defaultCenter = { lat: -23.550520, lng: -46.633308 };
    
    if (center) return center;
    if (waypoints.length > 0 && waypoints[0].location) return waypoints[0].location;
    
    return defaultCenter;
  }, [center, waypoints]);

  // Inicializar o mapa
  useEffect(() => {
    console.log('Inicializando mapa simplificado...');
    
    // Verificar se o Leaflet foi carregado
    if (!L) {
      setError('Biblioteca de mapas não disponível.');
      setLoading(false);
      return;
    }

    // Verificar se o elemento DOM existe
    if (!mapRef.current) {
      setError('Elemento do mapa não encontrado.');
      setLoading(false);
      return;
    }

    // Limpar o mapa anterior se existir
    if (mapInstanceRef.current) {
      console.log('Removendo mapa anterior...');
      try {
        mapInstanceRef.current.remove();
      } catch (e) {
        console.warn('Erro ao remover mapa anterior:', e);
      }
      mapInstanceRef.current = null;
    }

    try {
      console.log('Criando nova instância do mapa...');
      
      // Criar o mapa
      mapInstanceRef.current = L.map(mapRef.current, {
        center: [mapCenter.lat, mapCenter.lng],
        zoom: zoom,
        layers: [
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          })
        ]
      });

      console.log('Mapa criado com sucesso');

      // Filtrar waypoints válidos
      const validWaypoints = waypoints.filter(wp => (
        wp && wp.location && 
        typeof wp.location.lat === 'number' && 
        typeof wp.location.lng === 'number'
      ));

      console.log(`Processando ${validWaypoints.length} waypoints válidos`);

      // Criar ícones para os marcadores
      const startIcon = new L.Icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      const stopIcon = new L.Icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      });

      // Adicionar marcadores e criar pontos para a linha
      const points: [number, number][] = [];
      const markers: any[] = [];

      if (validWaypoints.length > 0) {
        validWaypoints.forEach((waypoint, index) => {
          try {
            const isStart = index === 0;
            const isEnd = index === validWaypoints.length - 1;
            
            const lat = parseFloat(waypoint.location.lat.toString());
            const lng = parseFloat(waypoint.location.lng.toString());
            
            // Verificar se as coordenadas são válidas
            if (isNaN(lat) || isNaN(lng)) {
              console.warn(`Coordenadas inválidas para waypoint ${index}:`, waypoint.location);
              return; // Pular este waypoint
            }
            
            // Adicionar ponto para a linha
            points.push([lat, lng] as [number, number]);
            
            // Criar e adicionar o marcador
            const marker = L.marker(
              [lat, lng],
              { icon: isStart ? startIcon : (isEnd ? stopIcon : stopIcon) }
            ).addTo(mapInstanceRef.current);
            
            // Configurar popup com informações do waypoint
            const title = waypoint.title || `Ponto ${index + 1}`;
            const address = waypoint.address || '';
            marker.bindPopup(`<b>${title}</b>${address ? `<br>${address}` : ''}`);
            
            // Armazenar referência ao marcador
            markers.push(marker as any);
            
            console.log(`Marcador adicionado para waypoint ${index}: ${title}`);
            
            // Abrir popup do primeiro e último ponto
            if (isStart || isEnd) {
              marker.openPopup();
            }
          } catch (markerError) {
            console.warn(`Erro ao adicionar marcador para waypoint ${index}:`, markerError);
          }
        });

        // Desenhar linha entre os pontos
        if (points.length >= 2) {
          try {
            console.log('Desenhando linha entre os pontos...');
            
            const polyline = L.polyline(points, {
              color: '#3388ff',
              weight: 5,
              opacity: 0.7,
              lineJoin: 'round'
            }).addTo(mapInstanceRef.current);
            
            // Ajustar o zoom para mostrar toda a linha
            mapInstanceRef.current.fitBounds(polyline.getBounds(), {
              padding: [50, 50]
            });
            
            console.log('Linha desenhada com sucesso');
          } catch (lineError) {
            console.error('Erro ao desenhar linha:', lineError);
          }
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Erro ao inicializar o mapa:', err);
      // @ts-ignore
      setError(`Não foi possível carregar o mapa: ${err.message || 'Erro desconhecido'}`);
      setLoading(false);
    }

    // Limpar recursos ao desmontar o componente
    return () => {
      console.log('Desmontando componente de mapa...');
      
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (e) {
          console.warn('Erro ao destruir mapa:', e);
        }
        mapInstanceRef.current = null;
      }
    };
  }, [waypoints, mapCenter, zoom]);

  // Renderizar o componente
  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      {/* Indicador de carregamento */}
      {loading && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.7)',
          zIndex: 1000
        }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={60} />
            <Typography variant="body2" sx={{ mt: 2 }}>Carregando mapa...</Typography>
          </Box>
        </Box>
      )}
      
      {/* Mensagem de erro */}
      {error && (
        <Box sx={{ 
          position: 'absolute', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          bgcolor: 'rgba(255,255,255,0.9)',
          zIndex: 1000,
          p: 2
        }}>
          <Box sx={{ maxWidth: '80%', textAlign: 'center', bgcolor: 'error.light', p: 3, borderRadius: 2 }}>
            <Typography variant="h6" color="error.contrastText" gutterBottom>Erro ao carregar mapa</Typography>
            <Typography color="error.contrastText">{error}</Typography>
          </Box>
        </Box>
      )}
      
      <div 
        ref={mapRef} 
        style={{ width: '100%', height: '100%', minHeight: '400px' }}
      />
    </Box>
  );
};

export default SimpleRouteMap;
